import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../../api/index"; // axios instance
import { jwtDecode } from "jwt-decode";

import { IoMdArrowBack } from "react-icons/io";
import { FiCreditCard, FiShield, FiZap, FiAlertCircle } from "react-icons/fi";

const FundWalletPage = () => {
  const navigate = useNavigate();
  const [fundAmount, setFundAmount] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
const [loadingXara, setLoadingXara] = useState(false);
  const token = localStorage.getItem("token");
  const decoded = jwtDecode(token);
  const userId = decoded.id || decoded._id;
  const email = decoded.Email || decoded.email;

  // ✅ Handles Payment via KoraPay
  const handleConfirmPayment = async () => {
    const amount = parseInt(fundAmount);

    if (isNaN(amount) || amount <= 0) {
      setError("Please enter a valid amount greater than 0");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Call backend to initialize payment
      const res = await api.koraPayapi({ email, amount, userId });

      if (res.data.checkout_url) {
        window.open(res.data.checkout_url, "_blank");
      } else {
        setError("Failed to get Paystack checkout URL.");
      }
    } catch (err) {
      console.error(err);
      setError("Payment initialization failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayWithXara = async () => {
  const amount = parseInt(fundAmount);

  if (isNaN(amount) || amount < 100) {
    setError("Please enter at least ₦100");
    return;
  }

  setLoadingXara(true);
  setError("");

  try {
    const res = await api.xaraFundWallet({
      userId,
      amount,
    });

    if (res.data?.checkout_url) {
      window.open(res.data.checkout_url, "_blank");
    } else {
      setError("Failed to generate Xara payment link.");
    }
  } catch (err) {
    console.error("Xara Fund Error:", err);

    setError(
      err?.response?.data?.message ||
      "Xara payment initialization failed. Please try again."
    );
  } finally {
    setLoadingXara(false);
  }
};

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      {/* 🔝 Sticky Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition text-gray-700 flex items-center gap-1"
        >
          <IoMdArrowBack size={24} />
          <span className="text-[10px] font-black uppercase tracking-widest">Back</span>
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight pr-10">Add Funds</h1>
        <div />
      </div>

      <div className="max-w-md mx-auto p-6 pt-10">
        {/* 💳 Visual Wallet Card */}
        <div className="bg-orange-600 p-8 rounded-[2.5rem] shadow-2xl shadow-orange-200 mb-10 text-white relative overflow-hidden">
          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <FiZap className="mb-4 text-orange-200" size={32} />
            <h2 className="text-2xl font-black mb-1">Instant Top-up</h2>
            <p className="text-orange-100 text-[10px] font-bold uppercase tracking-[0.2em]">Secured Payment Gateway</p>
          </div>
        </div>

        <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 space-y-8">
          {/* Header */}
          <div className="text-center">
            <h3 className="text-xl font-black text-gray-900">Enter Amount</h3>
            <p className="text-gray-400 text-xs font-medium mt-1">Funds will be added to your wallet immediately</p>
          </div>

          {/* Input Section */}
          <div className="relative">
            <span className="absolute left-6 top-1/2 -translate-y-1/2 text-xl font-black text-gray-400">₦</span>
            <input
              type="number"
              placeholder="0.00"
              value={fundAmount}
              onChange={(e) => setFundAmount(e.target.value)}
              className="w-full bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white rounded-[1.5rem] py-5 px-12 text-2xl font-black text-gray-900 transition-all outline-none"
            />
          </div>

          {/* Security Note */}
          <div className="flex items-start gap-3 bg-blue-50 p-4 rounded-2xl border border-blue-100">
            <FiShield className="text-blue-600 mt-1 shrink-0" size={18} />
            <p className="text-[11px] font-bold text-blue-800 leading-relaxed">
Your transaction is encrypted. You will be redirected to a secure payment gateway to complete your payment.
</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-xl border border-red-100 animate-pulse">
              <FiAlertCircle size={16} />
              <p className="text-[11px] font-black uppercase">{error}</p>
            </div>
          )}

          {/* Payment Button */}
          <button
            onClick={handleConfirmPayment}
            disabled={loading}
            className={`w-full py-5 rounded-[1.5rem] font-black uppercase tracking-widest text-sm transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2 ${
              loading 
                ? "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none" 
                : "bg-gray-900 text-white hover:bg-black shadow-gray-300"
            }`}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
                Processing...
              </>
            ) : (
              <>
                <FiCreditCard size={18} />
                Pay with Paystack
              </>
            )}
          </button>

<button
  onClick={handlePayWithXara}
  disabled={loading || loadingXara}
  className={`w-full py-5 rounded-[1.5rem] font-black uppercase tracking-widest text-sm transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2 ${
    loading || loadingXara
      ? "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none"
      : "bg-orange-600 text-white hover:bg-orange-700 shadow-orange-200"
  }`}
>
  {loadingXara ? (
    <>
      <div className="w-4 h-4 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
      Processing...
    </>
  ) : (
    <>
      <FiZap size={18} />
      Pay with Xara
    </>
  )}
</button>
        </div>

        {/* Footer Support Info */}
        <p className="text-center mt-10 text-[10px] font-black text-gray-300 uppercase tracking-widest leading-loose">
          Secure Payments • Instant Fulfillment • OurASAP
        </p>
      </div>
    </div>
  );
};

export default FundWalletPage;